import { useEffect, useRef, useState } from 'react'
import type { ViewportTransform } from '@arkturian/canvas-engine'

/**
 * DOM-Overlay-Bridge: keeps a layer of absolutely-positioned DOM
 * children glued to the canvas viewport transform.
 *
 * The Canvas2D backend draws frames + edges in world-space; this hook
 * makes the DOM-text-overlay layer track that world-space using a CSS
 * matrix transform on a single container. Per Codex' review, prefer a
 * matrix-based overlay-transform over per-element transforms — keeps
 * sub-pixel-drift consistent across browsers (Safari/Chromium handle
 * `transform-origin` slightly differently on per-element transforms).
 *
 * Usage:
 *
 *   const { overlayRef } = useDomOverlay(viewport)
 *   return (
 *     <div className="canvas-stage">
 *       <canvas ref={canvasRef} />
 *       <div ref={overlayRef} className="canvas-overlay">
 *         {blocks.map(b => (
 *           <div key={b.id} style={{ position:'absolute', left:b.x, top:b.y, width:b.w, height:b.h }}>
 *             ...editable text
 *           </div>
 *         ))}
 *       </div>
 *     </div>
 *   )
 *
 * Children position themselves in WORLD coordinates (the same coords
 * the layouter wrote into LayoutNode<TextBlock>). The container's
 * transform applies the world→screen mapping once for the whole layer.
 *
 * DPR / retina: the canvas-engine's ViewportTransform exposes pan/zoom
 * in CSS pixels; we apply the same CSS-pixel transform here so the DOM
 * stays glued without DPR-corrections at the overlay layer.
 */
export interface UseDomOverlayResult {
  overlayRef: React.RefObject<HTMLDivElement | null>
  /** Current world→screen scale as last applied to the overlay. */
  scale: number
}

export function useDomOverlay(
  viewport: ViewportTransform | null,
): UseDomOverlayResult {
  const overlayRef = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState(1)

  useEffect(() => {
    if (!viewport) return
    let rafId: number | null = null

    const tick = () => {
      const el = overlayRef.current
      if (!el) {
        rafId = requestAnimationFrame(tick)
        return
      }
      // ViewportTransform: world point (wx,wy) maps to screen as
      //   sx = wx * zoom + offsetX
      //   sy = wy * zoom + offsetY
      // CSS matrix(a,b,c,d,tx,ty) computes
      //   sx = a*x + c*y + tx
      // so a = d = zoom, b = c = 0, tx = offsetX, ty = offsetY.
      // ViewportTransform exposes these as `scale`, `offset` (Vector2)
      // — read via the value accessors that match the existing engine
      // conventions used by CanvasRenderer.
      const vp = viewport as unknown as {
        scale?: number
        offset?: { x: number; y: number }
        zoom?: number
        pan?: { x: number; y: number }
      }
      const z = vp.scale ?? vp.zoom ?? 1
      const ox = vp.offset?.x ?? vp.pan?.x ?? 0
      const oy = vp.offset?.y ?? vp.pan?.y ?? 0
      el.style.transformOrigin = '0 0'
      el.style.transform = `matrix(${z}, 0, 0, ${z}, ${ox}, ${oy})`
      if (z !== scale) setScale(z)
      rafId = requestAnimationFrame(tick)
    }

    rafId = requestAnimationFrame(tick)
    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId)
    }
  }, [viewport, scale])

  return { overlayRef, scale }
}
